import React, { Fragment } from 'react';
import { Ad } from 'Components/atoms/Ad';
import { getNativeAd } from 'SharedPartials/getNativeAd';

import { getArticleContent } from 'SharedHelpers/getArticleContent';
import { getIndexPageAdConfig } from 'SharedHelpers/getIndexPageAdConfig';
import { getIndexPageMobileAdConfig } from 'SharedHelpers/getIndexPageMobileAdConfig';
import { getSplitIndexContent } from 'SharedPartials/getSplitIndexContent';

import { getNewsletter } from 'SharedPartials/getNewsletter';
import { getPromobox } from 'SharedPartials/getPromobox';

/**
 * @name getIndexPageItems
 * @param adCountStart
 * @param articleCardProps
 * @param articlesPerRow
 * @param config
 * @param extraClassNames
 * @param nativeAdPosition
 * @param newsletterPosition
 * @param pages
 * @param promoboxPosition
 * @param setPageRef
 * @param templateType
 * @param title
 */
export const getIndexPageItems = ({
  adCountStart = {
    desktop: 0,
    mobile: 0,
  },
  articleCardProps = { datePosition: 'bottom' },
  articlesPerRow = 3,
  config,
  extraClassNames = {},
  nativeAdPosition = 6,
  newsletterPosition = 9,
  pages,
  promoboxPosition = 5,
  setPageRef,
  templateType = 'Article',
  title = '',
  isMobile,
}) => {
  // Get start position and iterate per item.
  let inlineAdPositionDesktop = adCountStart?.desktop;
  let inlineAdPositionMobile = adCountStart?.mobile;

  // Count ads per page.
  let adCounterMobile = adCountStart?.desktop;
  let adCounterDesktop = adCountStart?.mobile;

  return (
    Object.entries(pages)
      .map(([page, items]) => {
        const content = items.map(getArticleContent);

        const { newsletterConfig, promoBoxConfig, adBreakPoints } = config;
        const totalContent = content.length;

        const modifiedContent = content;

        // For the first content item, disable lazy loading and set isLCPCandidate to true.
        if (totalContent > 0 && modifiedContent[0].image) {
          modifiedContent[0].image.lazyLoad = false;
          modifiedContent[0].image.isLCPCandidate = true;
        }

        // Add polaris__core-content-first class to the first block.
        const groupClasses = { ...extraClassNames };
        groupClasses['polaris__core-content-index'] = true;

        const isFirst = Number.parseInt(page, 10) === 1;

        if (isFirst && templateType === 'indexTemplate') {
          const nativeAd = [
            <React.Fragment key="native_ad">
              {getNativeAd({
                id: 'native_index',
                targeting: {
                  position: 'native_index',
                  placement: 'native_index',
                  refresh: 'no',
                },
              })}
            </React.Fragment>,
          ];

          // Inject native ad in 5th position.
          modifiedContent.splice(nativeAdPosition, 0, { component: nativeAd });

          // Check if we have enough results.
          if (promoboxPosition && totalContent >= promoboxPosition) {
            const promobox = getPromobox({
              ...promoBoxConfig,
            });

            // Add promobox component.
            modifiedContent.splice(promoboxPosition, 0, { component: promobox });
          }

          if (newsletterPosition && totalContent >= newsletterPosition) {
            const newsletter = {
              props: {},
              newsletterConfig,
              additionalProps: { extraClassNames: newsletterConfig.extraClassNames },
            };

            const newsletterComponent = getNewsletter(newsletter);

            // Add newsletter component.
            modifiedContent.splice(newsletterPosition, 0, { component: newsletterComponent });
          }
        }

        // Add Ad component.
        // Default breakpoints.
        let desktopAdPoints = adBreakPoints.default.desktop.default;
        let mobileAdPoints = adBreakPoints.default.mobile.default;
        // First page specific breakpoints.
        if (isFirst) {
          desktopAdPoints = adBreakPoints.default.desktop.first;
          mobileAdPoints = adBreakPoints.default.mobile.first;
        }

        // Template specific breakpoints.
        if (adBreakPoints[templateType]) {
          desktopAdPoints = adBreakPoints[templateType].desktop.default;
          mobileAdPoints = adBreakPoints[templateType].mobile.default;
          if (isFirst) {
            desktopAdPoints = adBreakPoints[templateType].desktop.first;
            mobileAdPoints = adBreakPoints[templateType].mobile.first;
          }
        }

        const adverts = desktopAdPoints.filter(
          item => item <= totalContent,
        ).map((e) => {
          adCounterDesktop += 1;

          // increase the count
          // if it hits the length of the array, reset count
          inlineAdPositionDesktop += 1;
          if (inlineAdPositionDesktop > 10) inlineAdPositionDesktop = 1;

          return ({
            name: 'Desktop Ad',
            placement: e,
            component: (
              <Ad
                {...getIndexPageAdConfig(inlineAdPositionDesktop)}
                id={`refresh-inline_${adCounterDesktop}`}
                isSkippable
                isSpaceReserved
                key={`inlineAdDesktop_${adCounterDesktop}`}
                targetViewport="desktop"
              />
            ),
          });
        }).concat(
          mobileAdPoints.filter(item => item <= totalContent)
            .map((e) => {
              adCounterMobile += 1;

              // Increase the count.
              // If it hits the length of the array, reset count.
              inlineAdPositionMobile += 1;
              if (inlineAdPositionMobile > 10) inlineAdPositionMobile = 1;

              return ({
                name: 'Mobile Ad',
                placement: e,
                component: (
                  <Ad
                    {...getIndexPageMobileAdConfig(inlineAdPositionMobile)}
                    id={`refresh-inline_${adCounterMobile}`}
                    isSkippable
                    isSpaceReserved
                    key={`inlineAdMobile_${adCounterMobile}`}
                    targetViewport="mobile"
                  />
                ),
              });
            }),
        );

        const contentWithAds = [...modifiedContent];
        adverts.sort((a, b) => (a.placement > b.placement ? 1 : -1))
          .map((e, i) => contentWithAds.splice(e.placement + i, 0, e));

        const articleGroupedContent = getSplitIndexContent({
          content: contentWithAds,
          title,
          extraClassNames: groupClasses,
          articlesPerRow,
          articleCardProps,
          isMobile,
        });

        return (
          <Fragment key={`page-${page}`}>
            <div data-page={page} ref={setPageRef}>
              {articleGroupedContent}
            </div>
          </Fragment>
        );
      })
  );
};
